import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QrCode, Search } from "lucide-react";

import { BrandMark } from "@/components/BrandMark";
import { api } from "@/lib/api-client";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";

const TEXT = {
  it: {
    kicker: "Verifica un certificato",
    title: "Trova il tuo certificato",
    intro:
      "Inserisci il codice Surface ID stampato sul certificato, oppure inquadra il codice QR con la fotocamera del telefono: si apre il record digitale della superficie.",
    placeholder: "VRS-2026-ES0001",
    action: "Verifica",
    busy: "Ricerca…",
    hint: "Il codice si trova in alto a sinistra, accanto a «Surface ID / Numero di serie».",
    notFound: "Nessun certificato trovato con questo codice.",
    staff: "Area riservata",
    hero: "L'identità e la storia di ogni progetto VERSUS autentico.",
    heroSub:
      "Un unico record collega il certificato numerato ai lotti di materiale, all'applicatore autorizzato, alle date di posa e collaudo e alla manutenzione programmata.",
  },
  en: {
    kicker: "Verify a certificate",
    title: "Find your certificate",
    intro:
      "Enter the Surface ID printed on the certificate, or scan the QR code with your phone camera to open the surface's digital record.",
    placeholder: "VRS-2026-ES0001",
    action: "Verify",
    busy: "Searching…",
    hint: "The code is at the top left, next to “Surface ID / Serial number”.",
    notFound: "No certificate found for that code.",
    staff: "Staff area",
    hero: "The identity and history of every genuine VERSUS project.",
    heroSub:
      "One record links the numbered certificate to material batches, the authorised applicator, installation and inspection dates, and the maintenance programme.",
  },
  es: {
    kicker: "Verificar un certificado",
    title: "Encuentra tu certificado",
    intro:
      "Introduce el código Surface ID impreso en el certificado, o escanea el código QR con la cámara del teléfono para abrir el registro digital de la superficie.",
    placeholder: "VRS-2026-ES0001",
    action: "Verificar",
    busy: "Buscando…",
    hint: "El código está arriba a la izquierda, junto a «Surface ID / Número de serie».",
    notFound: "No se ha encontrado ningún certificado con ese código.",
    staff: "Área reservada",
    hero: "La identidad y la historia de cada proyecto VERSUS auténtico.",
    heroSub:
      "Un único registro conecta el certificado numerado con los lotes de material, el aplicador autorizado, las fechas de instalación e inspección y el programa de mantenimiento.",
  },
} as const;

export default function HomePage() {
  const { locale } = useLocale();
  const tx = TEXT[locale];
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await api.lookupCertificate(code.trim());
      navigate(`/verify/${token}`);
    } catch {
      setError(tx.notFound);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-surface min-h-screen px-5 py-10 sm:px-8">
      <div className="mx-auto max-w-3xl">
        <header className="flex items-center gap-4">
          <BrandMark className="h-14 w-14" />
          <div>
            <div className="brand-wordmark text-sm text-paper">VERSUS</div>
            <div className="brand-kicker">Surface ID</div>
          </div>
        </header>

        <hr className="hairline my-8" />

        <h1 className="font-serif text-3xl leading-tight text-paper sm:text-4xl">
          {tx.hero}
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-ice/80">
          {tx.heroSub}
        </p>

        <section className="glass-panel-strong mt-8 p-6 sm:p-7">
          <div className="brand-kicker">{tx.kicker}</div>
          <h2 className="mt-1 font-serif text-xl text-paper">{tx.title}</h2>
          <p className="mt-2 max-w-xl text-[0.8rem] leading-relaxed text-ice/70">
            {tx.intro}
          </p>

          <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ice/40" />
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={tx.placeholder}
                aria-label={tx.title}
                className="field num pl-11 uppercase tracking-[0.08em]"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <button
              type="submit"
              disabled={busy || !code.trim()}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-full bg-magenta px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-paper transition hover:brightness-110",
                (busy || !code.trim()) && "opacity-60",
              )}
            >
              {busy ? tx.busy : tx.action}
            </button>
          </form>

          {error && <p className="mt-3 text-xs text-pink">{error}</p>}

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.68rem] text-ice/50">
            <span className="inline-flex items-center gap-1.5">
              <QrCode className="h-3.5 w-3.5" />
              {tx.hint}
            </span>
          </div>
        </section>

        <div className="mt-8 flex flex-wrap items-center gap-4 text-[0.7rem]">
          <Link to="/login" className="text-pink hover:underline">
            {tx.staff} →
          </Link>
          <Link to="/projects" className="text-ice/50 hover:text-paper">
            {locale === "it" ? "Cantieri" : locale === "es" ? "Obras" : "Projects"}
          </Link>
        </div>
      </div>
    </div>
  );
}
